import type { ApolloClient } from "@apollo/client";
import type { Dispatch } from "redux";

import { employerConstants, payrollConstants } from "./types";
import {
  employerService,
  getPaginatedInvites as getInviteSet,
  submitEmployerAgents,
} from "services/employerService";
import ActionCreator from "utils/actionHandler";
import { normalizeRosterDobs } from "utils/fieldValidators";
import { map } from "lodash";

export const registerEmployer = (
  client: ApolloClient<object>,
  params: Parameters<typeof employerService.register>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      NonNullable<
        Awaited<ReturnType<typeof employerService.register>>
      >["company"]
    >(employerConstants.REGISTER_EMPLOYER);
    dispatch(actionCreator.request());

    return employerService.register(client, params).then(
      (data) => {
        if (data?.company?.id) {
          try {
            dispatch(actionCreator.success(data.company));
            // Success, navigate to the next page

            return data;
          } catch (error) {
            dispatch(actionCreator.failure(error));
          }
        }
      },
      (error: Error) => {
        dispatch(actionCreator.failure(error));
      }
    );
  };
};

//Add Controller + Beneficial Owner
export const postEmployerAgent = (
  client: ApolloClient<object>,
  employer: Parameters<typeof submitEmployerAgents>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof submitEmployerAgents>>
    >(employerConstants.REGISTER_AGENT);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      submitEmployerAgents(client, employer)
    );
  };
};

// Get company details
export const getCompanyInfo = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.getCompany>>
    >(employerConstants.GET_COMPANY);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getCompany(client)
    );
  };
};

// Get employer accounts
export const getAllEmployerAccounts = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.getAllEmployerAccounts>>
    >(employerConstants.GET_EMPLOYER_ACCOUNTS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getAllEmployerAccounts(client)
    );
  };
};

// Add employer groups
export const addEmployerGroup = (
  client: ApolloClient<object>,
  id: string,
  group: Parameters<typeof employerService.addEmployerGroup>[2]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.addEmployerGroup>>
    >(employerConstants.ADD_EMPLOYER_GROUPS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.addEmployerGroup(client, id, group),
      { id }
    );
  };
};

// Get employer groups
export const getAllEmployerGroups = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.getAllEmployerGroups>>
    >(employerConstants.GET_EMPLOYER_GROUPS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getAllEmployerGroups(client)
    );
  };
};
export const getEmployerGroup = (
  client: ApolloClient<object>,
  groupId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.getEmployerGroup>>
    >(employerConstants.GET_EMPLOYER_GROUP);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getEmployerGroup(client, groupId)
    );
  };
};

// Delete employer Group
export const deleteEmployerGroup = (
  client: ApolloClient<object>,
  params: Parameters<typeof employerService.deleteEmployerGroup>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.deleteEmployerGroup>>
    >(employerConstants.DELETE_EMPLOYER_GROUPS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.deleteEmployerGroup(client, params),
      { id: params.id }
    );
  };
};

// Update employer groups
export const updateEmployerGroup = (
  client: ApolloClient<object>,
  group: Parameters<typeof employerService.updateEmployerGroup>[1],
  companyId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.updateEmployerGroup>>
    >(employerConstants.UPDATE_EMPLOYER_GROUPS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.updateEmployerGroup(client, group, companyId)
    );
  };
};

export const inviteEmployees = (
  client: ApolloClient<object>,
  {
    userEmails,
    groupId,
  }: {
    userEmails: Parameters<typeof employerService.inviteEmployees>[1];
    groupId: string;
  }
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.inviteEmployees>>
    >(employerConstants.INVITE_EMPLOYEE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.inviteEmployees(client, userEmails, groupId)
    );
  };
};

export const stageEmployeeInvites = (
  client: ApolloClient<object>,
  params: Parameters<typeof employerService.stageEmployeeInvites>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.stageEmployeeInvites>>
    >(employerConstants.STAGE_EMPLOYEE_INVITE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.stageEmployeeInvites(client, params)
    );
  };
};

export const resendInvitation = (
  client: ApolloClient<object>,
  invitation: Parameters<typeof employerService.resendInvitation>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.resendInvitation>>
    >(employerConstants.RESEND_INVITE_EMPLOYEE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.resendInvitation(client, invitation)
    );
  };
};

export const getEmployeeInvitations = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.getEmployeeInvites>>
    >(employerConstants.GET_EMPLOYEE_INVITES);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getEmployeeInvites(client)
    );
  };
};

export const getTakeRateEmployeesForCompany = (
  client: ApolloClient<object>,
  groupId: string,
  timeframe: Parameters<
    typeof employerService.getTakeRateEmployeesForCompany
  >[2]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.getTakeRateEmployeesForCompany>>
    >(employerConstants.GET_TAKE_RATE_EMPLOYEES);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getTakeRateEmployeesForCompany(client, groupId, timeframe)
    );
  };
};

export const getLinkedEmployees = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.getLinkedEmployees>>
    >(employerConstants.GET_LINKED_EMPLOYEES);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getLinkedEmployees(client)
    );
  };
};

export const getPendingLinkedEmployees = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.getPendingLinkedEmployees>>
    >(employerConstants.GET_PENDING_LINKED_EMPLOYEES);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getPendingLinkedEmployees(client)
    );
  };
};

export const approveEmployeeLinks = (
  client: ApolloClient<object>,
  linkIds: Parameters<typeof employerService.approvePendingLinkedEmployees>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.approvePendingLinkedEmployees>>
    >(employerConstants.APPROVE_PENDING_LINKED_EMPLOYEE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.approvePendingLinkedEmployees(client, linkIds)
    );
  };
};

export const rejectEmployeeLinks = (
  client: ApolloClient<object>,
  linkIds: Parameters<typeof employerService.rejectPendingLinkedEmployees>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.rejectPendingLinkedEmployees>>
    >(employerConstants.REJECT_PENDING_LINKED_EMPLOYEE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.rejectPendingLinkedEmployees(client, linkIds)
    );
  };
};

export const processPayrollForGroup = (
  client: ApolloClient<object>,
  payrollInput: Parameters<typeof employerService.processPayroll>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.processPayroll>>
    >(employerConstants.PROCESS_PAYROLL);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.processPayroll(client, payrollInput)
    );
  };
};

export const getUsersForGroup = (
  client: ApolloClient<object>,
  groupId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.getUserProfilesForGroup>>
    >(employerConstants.GET_USERS_FOR_GROUP);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getUserProfilesForGroup(client, groupId)
    );
  };
};

export const approveContributionChanges = (
  client: ApolloClient<object>,
  contributionChangeIds: Parameters<
    typeof employerService.approveContributionChanges
  >[1],
  groupId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.approveContributionChanges>>
    >(employerConstants.APPROVE_CONTRIBUTION_CHANGES);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.approveContributionChanges(
        client,
        contributionChangeIds,
        groupId
      )
    );
  };
};

export const rejectContributionChanges = (
  client: ApolloClient<object>,
  contributionChangeIds: Parameters<
    typeof employerService.rejectContributionChanges
  >[1],
  groupId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.rejectContributionChanges>>
    >(employerConstants.REJECT_CONTRIBUTION_CHANGES);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.rejectContributionChanges(
        client,
        contributionChangeIds,
        groupId
      )
    );
  };
};

export const deleteEmployerLinks = (
  client: ApolloClient<object>,
  contributionChangeIds: Parameters<
    typeof employerService.deleteEmployerLinks
  >[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.deleteEmployerLinks>>
    >(employerConstants.DELETE_EMPLOYER_LINKS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.deleteEmployerLinks(client, contributionChangeIds)
    );
  };
};

export const getAdministrators = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.getAdministrators>>
    >(employerConstants.GET_ADMINISTRATORS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getAdministrators(client)
    );
  };
};

export const resendAdministratorInvitation = (
  client: ApolloClient<object>,
  params: Parameters<typeof employerService.resendAdministratorInvite>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.resendAdministratorInvite>>
    >(employerConstants.RESEND_INVITE_ADMINISTRATOR);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.resendAdministratorInvite(client, params)
    );
  };
};

export const inviteAdministrator = (
  client: ApolloClient<object>,
  administrator: Parameters<typeof employerService.inviteAdministrator>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.inviteAdministrator>>
    >(employerConstants.INVITE_ADMINISTRATOR);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.inviteAdministrator(client, administrator)
    );
  };
};

export const lockAdministrator = (
  client: ApolloClient<object>,
  params: Parameters<typeof employerService.lockAdministrator>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.lockAdministrator>>
    >(employerConstants.LOCK_ADMINISTRATOR);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.lockAdministrator(client, params)
    );
  };
};

export const unlockAdministrator = (
  client: ApolloClient<object>,
  params: Parameters<typeof employerService.unlockAdministrator>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.unlockAdministrator>>
    >(employerConstants.UNLOCK_ADMINISTRATOR);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.unlockAdministrator(client, params)
    );
  };
};

export const revokeInvitation = (
  client: ApolloClient<object>,
  invitationId: Parameters<typeof employerService.revokeInvitation>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.revokeInvitation>>
    >(employerConstants.REVOKE_INVITE_EMPLOYEE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.revokeInvitation(client, invitationId)
    );
  };
};

export const uploadEmployeesDocument = (
  client: ApolloClient<object>,
  groupId: string,
  file: Parameters<typeof employerService.uploadEmployeesDocumentRequest>[2]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.uploadEmployeesDocumentRequest>>
    >(employerConstants.UPLOAD_EMPLOYEES_DOCUMENT);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.uploadEmployeesDocumentRequest(client, groupId, file)
    );
  };
};

export const getEmployeeDocumentUploadJobs = (
  client: ApolloClient<object>,
  companyId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<
        ReturnType<typeof employerService.getEmployeeDocumentUploadJobsRequest>
      >
    >(employerConstants.GET_EMPLOYEE_DOCUMENT_UPLOAD_JOBS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getEmployeeDocumentUploadJobsRequest(client, companyId)
    );
  };
};

export const resendInvitations = (
  client: ApolloClient<object>,
  params: Parameters<typeof employerService.resendInvitations>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.resendInvitations>>
    >(employerConstants.RESEND_INVITES_EMPLOYEE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.resendInvitations(client, params)
    );
  };
};

export const getPaginatedEmployees = (
  client: ApolloClient<object>,
  params: Parameters<typeof employerService.getPaginatedEmployees>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.getPaginatedEmployees>>
    >(employerConstants.GET_PAGINATED_EMPLOYEES);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getPaginatedEmployees(client, params)
    );
  };
};

export const setEmployerSession = (
  client: ApolloClient<object>,
  companyId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.setEmployerSession>>
    >(employerConstants.SET_EMPLOYER_SESSION);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.setEmployerSession(client, companyId)
    );
  };
};
export const getLinkedCompanies = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.getLinkedCompanies>>
    >(employerConstants.GET_LINKED_COMPANIES);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getLinkedCompanies(client)
    );
  };
};

export const getPaginatedInvites = (
  client: ApolloClient<object>,
  params: Parameters<typeof getInviteSet>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getInviteSet>>
    >(employerConstants.GET_PAGINATED_INVITES);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getInviteSet(client, params)
    );
  };
};

export const changeAdministratorRole = (
  client: ApolloClient<object>,
  params: Parameters<typeof employerService.changeAdministratorRole>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.changeAdministratorRole>>
    >(employerConstants.CHANGE_ADMIN_ROLE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.changeAdministratorRole(client, params)
    );
  };
};

export const fetchOwners = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.getBeneficialOwners>>
    >(employerConstants.GET_OWNERS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getBeneficialOwners(client)
    );
  };
};

export const validateSelfServe = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.validateSelfServe>>
    >(employerConstants.VALIDATE_SELF_SERVE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.validateSelfServe(client)
    );
  };
};

export const getPaymentRequirement = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.getPaymentRequirement>>
    >(employerConstants.GET_PAYMENT_REQUIREMENT);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getPaymentRequirement(client)
    );
  };
};

export const getSetupIntent = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.getSetupIntent>>
    >(employerConstants.GET_SETUP_INTENT);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getSetupIntent(client)
    );
  };
};

export const createSetupIntent = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.createSetupIntent>>
    >(employerConstants.CREATE_SETUP_INTENT);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.createSetupIntent(client)
    );
  };
};

export const setInvoicePaymentMethod = (
  client: ApolloClient<object>,
  paymentMethod: Parameters<typeof employerService.setInvoicePaymentMethod>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.setInvoicePaymentMethod>>
    >(employerConstants.SET_INVOICE_PAYMENT_METHOD);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.setInvoicePaymentMethod(client, paymentMethod)
    );
  };
};

export const getBillingPortalURL = (
  client: ApolloClient<object>,
  returnUrl: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.getBillingPortalURL>>
    >(employerConstants.GET_BILLING_PORTAL_URL);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getBillingPortalURL(client, returnUrl)
    );
  };
};

export const getProgressiveOnboarding = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.getProgressiveOnboarding>>
    >(employerConstants.GET_PROGRESSIVE_ONBOARDING);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getProgressiveOnboarding(client)
    );
  };
};

export const updateProgressiveOnboarding = (
  client: ApolloClient<object>,
  eventName: Parameters<typeof employerService.updateProgressiveOnboarding>[1],
  companyId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.updateProgressiveOnboarding>>
    >(employerConstants.UPDATE_PROGRESSIVE_ONBOARDING);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.updateProgressiveOnboarding(client, eventName, companyId)
    );
  };
};

export const uploadRoster = (
  client: ApolloClient<object>,
  params: Parameters<typeof employerService.uploadRoster>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.uploadRoster>>
    >(employerConstants.UPLOAD_ROSTER);
    // Normalize every row's dob to the YYYY-MM-DD wire format here so all callers
    // (single-add MM/DD/YYYY, CSV bulk already-YYYY-MM-DD) ship a consistent format.
    const normalizedParams = {
      ...params,
      rows: normalizeRosterDobs(params.rows),
    };
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.uploadRoster(client, normalizedParams)
    );
  };
};

export const getEmployeeRoster = (
  client: ApolloClient<object>,
  params: Parameters<typeof employerService.getEmployeeRoster>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.getEmployeeRoster>>
    >(employerConstants.GET_EMPLOYEE_ROSTER);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getEmployeeRoster(client, params)
    );
  };
};

export const resyncEmployeeRoster = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.resyncEmployeeRoster>>
    >(employerConstants.RESYNC_ROSTER);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.resyncEmployeeRoster(client)
    );
  };
};

export const processRoster = (
  client: ApolloClient<object>,
  companyId?: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.processRoster>>
    >(employerConstants.PROCESS_ROSTER);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.processRoster(client, companyId)
    );
  };
};

export const approveRoster = (
  client: ApolloClient<object>,
  rejectedEmployeeIds: Parameters<typeof employerService.approveRoster>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.approveRoster>>
    >(employerConstants.APPROVE_ROSTER);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.approveRoster(client, rejectedEmployeeIds)
    );
  };
};

// Referred Company
export const referCompany = (client: ApolloClient<object>, email: string) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.referCompany>>
    >(employerConstants.REFER_COMPANY);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.referCompany(client, email)
    );
  };
};

export const getPayrollGroupContributionAbility = (
  client: ApolloClient<object>,
  groupId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<
        ReturnType<
          typeof employerService.getPayrollGroupContributionAbilityRequest
        >
      >
    >(employerConstants.GET_PAYROLL_GROUP_CONTRIBUTION_ABILITY);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getPayrollGroupContributionAbilityRequest(
        client,
        groupId
      ),
      { id: groupId }
    );
  };
};

export const currentPayrolls = (
  client: ApolloClient<object>,
  companyId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.currentPayrolls>>
    >(employerConstants.CURRENT_PAYROLLS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.currentPayrolls(client, companyId)
    );
  };
};

export const skipBilling = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.skipBilling>>
    >(employerConstants.SKIP_BILLING);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.skipBilling(client)
    );
  };
};

export const hydrateDashboard = (client: ApolloClient<object>) => {
  // partial mapping — hydrate responses include queries with no reducer fan-out
  const queryNameToReducerAction: Partial<Record<string, string>> = {
    employerAccountList: employerConstants.GET_EMPLOYER_ACCOUNTS_SUCCESS,
    getEmployerGroups: employerConstants.GET_EMPLOYER_GROUPS_SUCCESS,
    getAllEmployeeInvitesForCompany:
      employerConstants.GET_EMPLOYEE_INVITES_SUCCESS,
    getEmployerLinkRequests:
      employerConstants.GET_PENDING_LINKED_EMPLOYEES_SUCCESS,
    getTotalAmountSaved: payrollConstants.GET_TOTAL_AMOUNT_SAVED_SUCCESS,
    getLinkedEmployees: employerConstants.GET_LINKED_EMPLOYEES_SUCCESS,
  };
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.hydrateDashboard>>
    >(employerConstants.HYDRATE_DASHBOARD);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.hydrateDashboard(client),
      undefined,
      (data) => {
        map(data, (d, key) => {
          if (queryNameToReducerAction[key]) {
            const action = {
              type: queryNameToReducerAction[key],
              data: d,
            };
            dispatch(action);
          }
        });
      }
    );
  };
};

export const getEmployerRequiredUpdates = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<
        ReturnType<typeof employerService.getEmployerRequiredStepsRequest>
      >
    >(employerConstants.GET_EMPLOYER_REQUIRED_STEPS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getEmployerRequiredStepsRequest(client)
    );
  };
};

export const updateRequiredOrderForm = (
  client: ApolloClient<object>,
  completedOrderForm: Parameters<
    typeof employerService.updateRequiredOrderFormsRequest
  >[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<
        ReturnType<typeof employerService.updateRequiredOrderFormsRequest>
      >
    >(employerConstants.UPDATE_EMPLOYER_REQUIRED_ORDER_FORM);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.updateRequiredOrderFormsRequest(
        client,
        completedOrderForm
      )
    );
  };
};

export const getPayrollIntegrationStatus = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.getPayrollIntegrationStatus>>
    >(employerConstants.GET_PAYROLL_INTEGRATION_STATUS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getPayrollIntegrationStatus(client)
    );
  };
};

export const getCompanyTermsSubmissionDate = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.companyTermsDate>>
    >(employerConstants.GET_EMPLOYER_TERMS_DATE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.companyTermsDate(client)
    );
  };
};

export const getEmployerOrder = (
  client: ApolloClient<object>,
  signupCode: Parameters<typeof employerService.getEmployerOrder>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.getEmployerOrder>>
    >(employerConstants.GET_EMPLOYER_ORDER);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getEmployerOrder(client, signupCode)
    );
  };
};

export const getCompanyAgreements = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.getCompanyAgreements>>
    >(employerConstants.GET_COMPANY_AGREEMENTS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getCompanyAgreements(client)
    );
  };
};

export const getEmployerWalletDetails = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.getEmployerWalletDetails>>
    >(employerConstants.GET_EMPLOYER_WALLET_DETAILS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getEmployerWalletDetails(client)
    );
  };
};

export const activatePayrollIntegration = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<
        ReturnType<typeof employerService.activatePayrollIntegrationSetup>
      >
    >(employerConstants.ACTIVATE_PAYROLL_INTEGRATION);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.activatePayrollIntegrationSetup(client)
    );
  };
};

export const acknowledgeRecurringDebits = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.acknowledgeRecurringDebits>>
    >(employerConstants.ACKNOWLEDGE_RECURRING_DEBITS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.acknowledgeRecurringDebits(client)
    );
  };
};

export const getRecurringDebitsAcknowledgment = (
  client: ApolloClient<object>
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<
        ReturnType<typeof employerService.getRecurringDebitsAcknowledgment>
      >
    >(employerConstants.GET_RECURRING_DEBITS_ACKNOWLEDGMENT);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getRecurringDebitsAcknowledgment(client)
    );
  };
};

export const submitUkgConfig = (
  client: ApolloClient<object>,
  params: Parameters<typeof employerService.submitUkgConfig>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.submitUkgConfig>>
    >(employerConstants.SUBMIT_UKG_CONFIG);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.submitUkgConfig(client, params)
    );
  };
};

export const getUkgConfig = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof employerService.getUkgConfig>>
    >(employerConstants.GET_UKG_CONFIG);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      employerService.getUkgConfig(client)
    );
  };
};
